import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'

type UserInvite = {
  id: number
  token: string
  created_at: string
  expires_at: string
  expired: boolean
  used_at: string | null
  used_by: {
    id: number
    name: string
    email: string
  } | null
}

type Props = {
  onClose: () => void
}

export default function InviteFriends({ onClose }: Props) {
  const [invites, setInvites] = useState<UserInvite[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  useEffect(() => {
    fetchInvites()
  }, [])

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const fetchInvites = async () => {
    try {
      const response = await fetch('/api/v1/user_invites')
      const data = await response.json()
      setInvites(data.invites)
    } catch (err) {
      console.error('Error fetching invites:', err)
    } finally {
      setLoading(false)
    }
  }

  const generateInvite = async () => {
    setGenerating(true)
    try {
      const response = await fetchWithCsrf('/api/v1/user_invites', {
        method: 'POST'
      })

      if (response.ok) {
        const data = await response.json()
        setInvites([data.invite, ...invites])
        copyToClipboard(data.invite_url)
        setCopiedToken(data.invite.token)
        setTimeout(() => setCopiedToken(null), 3000)
      } else {
        const data = await response.json()
        alert(data.error || 'Failed to generate invite')
      }
    } catch (err) {
      console.error('Error generating invite:', err)
      alert('Failed to generate invite')
    } finally {
      setGenerating(false)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const copyInviteUrl = (invite: UserInvite) => {
    const url = `${window.location.origin}/signup?invite=${invite.token}`
    copyToClipboard(url)
    setCopiedToken(invite.token)
    setTimeout(() => setCopiedToken(null), 3000)
  }

  const activeInvites = invites.filter(inv => !inv.used_at && !inv.expired)
  const expiredInvites = invites.filter(inv => !inv.used_at && inv.expired)
  const usedInvites = invites.filter(inv => inv.used_at)

  const formatExpiresAt = (expiresAt: string) => {
    const date = new Date(expiresAt)
    const now = new Date()
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays <= 0) return 'Expired'
    if (diffDays === 1) return 'Expires tomorrow'
    if (diffDays <= 7) return `Expires in ${diffDays} days`
    return `Expires ${date.toLocaleDateString()}`
  }

  return (
    <div
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Invite Friends</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600 mb-4">
            Share a one-time invite link with friends. Each link can only be used once and expires after 30 days.
          </p>
          <button
            onClick={generateInvite}
            disabled={generating}
            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating...
              </>
            ) : (
              '+ Generate New Invite Link'
            )}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <>
              {activeInvites.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Active Invites ({activeInvites.length})</h3>
                  <div className="space-y-2">
                    {activeInvites.map((invite) => (
                      <div key={invite.id} className="bg-green-50 border border-green-200 rounded-md p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-gray-500">
                                Created {new Date(invite.created_at).toLocaleDateString()}
                              </p>
                              <span className="text-xs text-amber-600 font-medium">
                                {formatExpiresAt(invite.expires_at)}
                              </span>
                            </div>
                            <p className="text-xs font-mono text-gray-600 truncate mt-1">
                              {window.location.origin}/signup?invite={invite.token}
                            </p>
                          </div>
                          <button
                            onClick={() => copyInviteUrl(invite)}
                            className="ml-3 flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-green-300 text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            {copiedToken === invite.token ? (
                              <>
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Copied!
                              </>
                            ) : (
                              <>
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {expiredInvites.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Expired Invites ({expiredInvites.length})</h3>
                  <div className="space-y-2">
                    {expiredInvites.map((invite) => (
                      <div key={invite.id} className="bg-red-50 border border-red-200 rounded-md p-3 opacity-60">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500">
                              Created {new Date(invite.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-red-600 font-medium mt-1">
                              Expired {new Date(invite.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Expired
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {usedInvites.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Used Invites ({usedInvites.length})</h3>
                  <div className="space-y-2">
                    {usedInvites.map((invite) => (
                      <div key={invite.id} className="bg-gray-50 border border-gray-200 rounded-md p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{invite.used_by?.name}</p>
                            <p className="text-xs text-gray-500">{invite.used_by?.email}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Joined {new Date(invite.used_at!).toLocaleDateString()}
                            </p>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Used
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {invites.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No invites yet. Generate your first invite above!</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
